////////////////////////////////////////////////////////////////////////////////////
// Filtering
//
// Two operating modes, selected per-page based on the filter form's
// `data-client-side` attribute:
//
//   1. Client-side mode (`data-client-side="true"`): all dataset items are
//      rendered to the DOM up-front. JS toggles `.is-hidden` on cards by
//      the card attribute named in the grid's `data-filter-attr` (default
//      `range`, so cards match on `data-range`; a card may list several
//      space-separated values) and slices the visible set into pages of
//      `data-vt-page-size`. `data-vt-pagination-enabled="false"` on the grid
//      shows every match on one page. No AJAX/REST round-trip.
//
//   2. Server-side mode (default): JS sends a fetch request to either the
//      form's `data-rest-endpoint` (REST) or admin-ajax (legacy fallback),
//      replaces the cards grid with the response markup, and re-binds
//      pagination. An AbortController cancels in-flight requests when a
//      newer one fires (eliminates stale-response races on rapid filter
//      changes). A loading state (`.is-loading` + `aria-busy`) is applied
//      to the filter block during the request.
//
// Options on the filter form, shared by both modes:
//
//   - `data-preserve-params="a,b"`: query parameters read from the page URL
//     on load and kept in every URL the filter pushes (the kiosk keeps
//     `kiosk_return` this way).
//   - `data-validate-filter="true"`: ignore a `?filter` value that is not
//     one of the dropdown's options, instead of filtering to no results.
////////////////////////////////////////////////////////////////////////////////////

document.addEventListener('DOMContentLoaded', function () {
  const filterBlock = document.querySelector('.js-filter-block');
  const filterForm = filterBlock ? filterBlock.querySelector('.filter-block') : null;
  if (!filterForm) {
    return;
  }

  const showAllButton = filterBlock.querySelector('.js-filter-showall');
  const filterBlockPostType = filterForm.getAttribute('data-post-type') || 'post';
  const filterBlockRestEndpoint = filterForm.getAttribute('data-rest-endpoint') || '';
  const isClientSide = filterForm.getAttribute('data-client-side') === 'true';
  const validateFilter = filterForm.getAttribute('data-validate-filter') === 'true';
  const postsGrid = document.querySelector('.js-posts-cards');
  const paginationContainer = document.querySelector('.js-section-pagination');
  const paginationContainerInner = paginationContainer ? paginationContainer.querySelector('.js-pagination') : null;

  // Query parameters to carry through every filter URL, captured on load.
  const initialParams = new URLSearchParams(window.location.search);
  const preservedParams = (filterForm.getAttribute('data-preserve-params') || '')
    .split(',')
    .map(name => name.trim())
    .filter(name => name && initialParams.get(name))
    .map(name => [name, initialParams.get(name)]);

  /**
   * Add the preserved query parameters to a URLSearchParams object.
   *
   * @since 1.0.0
   * @param {URLSearchParams} params Parameters to extend in place.
   * @return {URLSearchParams} The same object.
   */
  function withPreservedParams(params) {
    preservedParams.forEach(([name, value]) => params.set(name, value));
    return params;
  }

  // Smooth-scroll the filter block into view, offset by the height of the
  // sticky <header>. We predict the SCROLLED-state header height (the
  // compact version) rather than the current height — without this, a
  // click from the top of the page targets a position calculated against
  // the tall header, but by the time the scroll completes the header has
  // collapsed to its short state, leaving the filter too far down. The
  // second click would then recompute and adjust, producing a visible
  // two-step jump.
  //
  // Approach: toggle `.scrolled` with transitions disabled, measure,
  // restore. Since the add/measure/remove happens synchronously inside
  // a single JS turn, no paint occurs in between, so the user sees no
  // flicker on the header itself.
  function scrollToFilterBlock() {
    if (!filterBlock) return;

    const header = document.querySelector('header');
    let headerHeight = 0;

    if (header) {
      const wasScrolled = header.classList.contains('scrolled');
      if (!wasScrolled) {
        header.style.transition = 'none';
        header.classList.add('scrolled');
      }
      headerHeight = header.getBoundingClientRect().height;
      if (!wasScrolled) {
        header.classList.remove('scrolled');
        header.style.transition = '';
      }
    }

    const breathingRoom = 16;
    const targetY = filterBlock.getBoundingClientRect().top + window.scrollY - headerHeight - breathingRoom;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
  }

  if (isClientSide) {
    initClientSideFilter();
  } else {
    initServerSideFilter();
  }

  ////////////////////////////////////////////////////////////////////////////////////
  // Client-side mode
  ////////////////////////////////////////////////////////////////////////////////////
  function initClientSideFilter() {
    if (!postsGrid) return;

    const cards = Array.from(postsGrid.querySelectorAll('.js-vt-card'));
    const filterAttr = 'data-' + (postsGrid.getAttribute('data-filter-attr') || 'range');
    const paginationEnabled = postsGrid.getAttribute('data-vt-pagination-enabled') !== 'false';
    const configuredPageSize = parseInt(postsGrid.getAttribute('data-vt-page-size'), 10) || 6;
    const pageSize = paginationEnabled ? configuredPageSize : Math.max(cards.length, 1);
    const resultCountEl = document.querySelector('.js-vt-result-count');
    const select = filterForm.querySelector('.js-filter-dropdown');

    function getStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get('paged'), 10);
      const requestedFilter = params.get('filter') || '';
      const isKnownFilter = !validateFilter || !requestedFilter || !select ||
        Array.from(select.options).some(option => option.value === requestedFilter);

      return {
        filter: isKnownFilter ? requestedFilter : '',
        page: paginationEnabled && !isNaN(page) && page > 0 ? page : 1
      };
    }

    function pushUrlState(filterValue, page) {
      const params = new URLSearchParams();
      if (filterValue) params.set('filter', filterValue);
      if (paginationEnabled && page > 1) params.set('paged', page);
      withPreservedParams(params);
      const qs = params.toString();
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      history.pushState({ filter: filterValue, page: page }, '', url);
    }

    function applyState(filterValue, page) {
      const matching = cards.filter(card => {
        const values = (card.getAttribute(filterAttr) || '').split(/\s+/).filter(Boolean);
        return !filterValue || values.includes(filterValue);
      });
      const totalPages = Math.max(1, Math.ceil(matching.length / pageSize));
      const currentPage = Math.max(1, Math.min(page, totalPages));
      const startIdx = (currentPage - 1) * pageSize;
      const endIdx = startIdx + pageSize;

      // Hide everything by default, then reveal the slice of matching cards.
      cards.forEach(card => card.classList.add('is-hidden'));
      matching.forEach((card, idx) => {
        if (idx >= startIdx && idx < endIdx) {
          card.classList.remove('is-hidden');
        }
      });

      renderPagination(currentPage, totalPages);

      if (resultCountEl) {
        const count = matching.length;
        resultCountEl.textContent = count === 1
          ? 'Showing 1 design'
          : `Showing ${count} designs`;
      }
    }

    // Mirror the markup shape of nous_build_ajax_pagination() so existing
    // CSS hooks apply unchanged. Each link gets a `data-page` attribute so
    // the click handler doesn't need to parse hrefs.
    function renderPagination(currentPage, totalPages) {
      if (!paginationEnabled || !paginationContainerInner) return;
      if (totalPages < 2) {
        paginationContainerInner.innerHTML = '';
        return;
      }

      const items = [];
      if (currentPage > 1) {
        items.push(`<li class="page-item"><a class="page-link" href="?paged=${currentPage - 1}" data-page="${currentPage - 1}" aria-label="Previous page"><span class="icon icon-arrow-left"></span></a></li>`);
      }
      for (let p = 1; p <= totalPages; p++) {
        if (p === currentPage) {
          items.push(`<li class="page-item active" aria-current="page"><span class="page-link">${p}<span class="visually-hidden">(current)</span></span></li>`);
        } else {
          items.push(`<li class="page-item"><a class="page-link" href="?paged=${p}" data-page="${p}">${p}</a></li>`);
        }
      }
      if (currentPage < totalPages) {
        items.push(`<li class="page-item"><a class="page-link" href="?paged=${currentPage + 1}" data-page="${currentPage + 1}" aria-label="Next page"><span class="icon icon-arrow-right"></span></a></li>`);
      }

      paginationContainerInner.innerHTML = `<nav class="text-center" aria-label="Page pagination"><ul class="pagination d-inline-flex mb-0">${items.join('')}</ul></nav>`;
    }

    // Initial state — honour ?filter and ?paged from the URL.
    const initial = getStateFromUrl();
    if (select) {
      select.value = initial.filter;
      if (window.jQuery) {
        window.jQuery(select).trigger('chosen:updated');
      }
    }
    applyState(initial.filter, initial.page);

    // Filter dropdown change.
    if (window.jQuery) {
      window.jQuery(filterForm).on('change', '.js-filter-dropdown', function () {
        const value = this.value || '';
        pushUrlState(value, 1);
        applyState(value, 1);
        setTimeout(scrollToFilterBlock, 300);
      });
    }

    // "All" button — reset filter.
    if (showAllButton) {
      showAllButton.addEventListener('click', function (e) {
        e.preventDefault();
        if (select) {
          select.selectedIndex = 0;
          select.value = '';
          if (window.jQuery) {
            window.jQuery(select).trigger('chosen:updated');
          }
        }
        pushUrlState('', 1);
        applyState('', 1);
        setTimeout(scrollToFilterBlock, 300);
      });
    }

    // Pagination clicks (delegated — pagination markup is replaced on each render).
    if (paginationEnabled && paginationContainer) {
      paginationContainer.addEventListener('click', function (e) {
        const link = e.target.closest('a[data-page]');
        if (!link) return;
        e.preventDefault();
        const page = parseInt(link.getAttribute('data-page'), 10) || 1;
        const current = getStateFromUrl();
        pushUrlState(current.filter, page);
        applyState(current.filter, page);
        setTimeout(scrollToFilterBlock, 300);
      });
    }

    // Back/forward — restore previous state.
    window.addEventListener('popstate', function () {
      const s = getStateFromUrl();
      if (select) {
        select.value = s.filter;
        if (window.jQuery) {
          window.jQuery(select).trigger('chosen:updated');
        }
      }
      applyState(s.filter, s.page);
    });
  }

  ////////////////////////////////////////////////////////////////////////////////////
  // Server-side mode
  ////////////////////////////////////////////////////////////////////////////////////
  function initServerSideFilter() {
    let currentRequest = null;

    function setLoading(isLoading) {
      // Toggle .is-loading on BOTH the filter block and the posts grid —
      // they're typically in sibling sections (not nested), so each owns
      // its own loading-state CSS rules rather than a shared selector.
      if (filterBlock) {
        filterBlock.classList.toggle('is-loading', isLoading);
      }
      if (postsGrid) {
        postsGrid.classList.toggle('is-loading', isLoading);
        if (isLoading) {
          postsGrid.setAttribute('aria-busy', 'true');
        } else {
          postsGrid.removeAttribute('aria-busy');
        }
      }
      const select = filterForm.querySelector('.js-filter-dropdown');
      if (select) {
        if (isLoading) {
          select.setAttribute('disabled', 'disabled');
          select.setAttribute('aria-disabled', 'true');
        } else {
          select.removeAttribute('disabled');
          select.removeAttribute('aria-disabled');
        }
      }
    }

    function filterPosts(page = 1) {
      const formData = new FormData(filterForm);
      formData.set('paged', page);

      const filteredFormData = new FormData();
      for (let [key, value] of formData.entries()) {
        if (value !== '' && value !== null && value !== undefined) {
          filteredFormData.append(key, value);
        }
      }

      const queryString = new URLSearchParams(filteredFormData).toString();

      // Cancel any prior in-flight request — eliminates stale-response races
      // on rapid filter changes.
      if (currentRequest) {
        currentRequest.abort();
      }
      currentRequest = new AbortController();

      // Prefer the form's data-rest-endpoint when set; fall back to the
      // legacy admin-ajax pattern otherwise.
      const requestUrl = filterBlockRestEndpoint
        ? `${window.location.origin}/wp-json/${filterBlockRestEndpoint}?${queryString}`
        : `${window.location.origin}/wp-admin/admin-ajax.php?action=nous_filter_${filterBlockPostType}&${queryString}`;

      setLoading(true);

      fetch(requestUrl, {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: currentRequest.signal
      })
        .then(response => response.json())
        .then(data => {
          // Normalise response shape: admin-ajax wraps the payload in
          // { success: true, data: {...} } whereas REST returns the payload
          // directly { cards_html, pagination_html, ... }.
          const payload = (data && data.success && data.data) ? data.data : data;
          if (!payload) return;

          if (postsGrid && payload.cards_html) {
            postsGrid.innerHTML = payload.cards_html;
          } else if (postsGrid) {
            postsGrid.innerHTML = '<p>No posts found.</p>';
          }

          if (paginationContainerInner && payload.pagination_html) {
            paginationContainerInner.innerHTML = payload.pagination_html;
            bindPaginationEvents();
          } else if (paginationContainerInner) {
            paginationContainerInner.innerHTML = '';
          }

          if (preservedParams.length) {
            const browserParams = withPreservedParams(new URLSearchParams(queryString));
            history.pushState(null, '', `${window.location.pathname}?${browserParams.toString()}`);
          } else {
            history.pushState(null, '', `?${queryString}`);
          }
        })
        .catch(error => {
          if (error.name === 'AbortError') return; // Superseded by a newer request.
          console.error('Filter request error:', error);
        })
        .finally(() => {
          // Only clear loading if the latest request is the one settling.
          // If a newer request superseded this one, it owns the loading state.
          if (currentRequest && currentRequest.signal.aborted === false) {
            setLoading(false);
          }
        });
    }

    function bindPaginationEvents() {
      if (!paginationContainer) return;
      const links = paginationContainer.querySelectorAll('a');
      links.forEach(link => {
        link.addEventListener('click', function (event) {
          event.preventDefault();
          const page = new URL(link.href).searchParams.get('paged');
          filterPosts(page);
          setTimeout(scrollToFilterBlock, 300);
        });
      });
    }

    if (window.jQuery) {
      window.jQuery(filterForm).on('change', '.js-filter-dropdown', function () {
        filterPosts(1);
        setTimeout(scrollToFilterBlock, 300);
      });

      window.jQuery(document).on('click', '.js-filter-showall', function (e) {
        e.preventDefault();
        const $form = window.jQuery(this).closest('form.filter-block');
        $form.find('.js-filter-dropdown').each(function () {
          this.selectedIndex = 0;
          window.jQuery(this).val('');
          window.jQuery(this).trigger('chosen:updated');
        });
        filterPosts(1);
        setTimeout(scrollToFilterBlock, 300);
      });
    }

    bindPaginationEvents();
  }
});
